import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Trash2, Minus, Plus, ClipboardCheck, Truck, CreditCard, Search } from "lucide-react";
import ShopNav from "../../components/shop/ShopNav";
import { api, mediaUrl, formatApiError } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";

const STEP_ICONS = [Search, ClipboardCheck, CreditCard, Truck];

export default function Cart() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { items, setQty, remove, subtotal } = useCart();
  const [c, setC] = useState({ name: "", email: "", phone: "", address: "", city: "", country: "MX", postal_code: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rates, setRates] = useState([]);
  const [selRate, setSelRate] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [quoteErr, setQuoteErr] = useState("");
  const set = (k) => (e) => setC({ ...c, [k]: e.target.value });

  const shippingCost = selRate ? Number(selRate.cost || 0) : 0;
  const grandTotal = subtotal + shippingCost;

  const quoteShipping = async () => {
    if (!c.postal_code.trim()) { setQuoteErr("Ingresa tu código postal para cotizar el envío"); return; }
    setQuoteErr(""); setQuoting(true); setRates([]); setSelRate(null);
    try {
      const { data } = await api.post("/shop/quote-shipping", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        postal_code: c.postal_code.trim(), city: c.city, country: c.country || "MX",
      });
      if (!data.rates?.length) { setQuoteErr("No hay tarifas disponibles para este destino. El equipo cotizará tu envío manualmente."); }
      else { setRates(data.rates); setSelRate(data.rates[0]); }
    } catch (e2) { setQuoteErr(formatApiError(e2.response?.data?.detail) || "No se pudo cotizar el envío"); }
    finally { setQuoting(false); }
  };

  const submitOrder = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/shop/request-order", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        customer: c,
        shipping: selRate || undefined,
      });
      navigate(`/shop/success?order=${data.code}`);
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Error"); setLoading(false); }
  };

  const steps = t("shop.process.steps");

  return (
    <div className="min-h-screen bg-cream text-navy">
      <ShopNav />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <h1 className="font-editorial text-4xl tracking-tight mb-8">{t("shop.cart")}</h1>
        {items.length === 0 ? (
          <div className="text-navy/50" data-testid="cart-empty">
            {t("shop.empty")} <Link to="/shop" className="text-violet hover:underline ml-2">{t("shop.continue")}</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-[1.4fr_1fr] gap-10">
            {/* items */}
            <div className="space-y-4" data-testid="cart-items">
              {items.map((i) => (
                <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 bg-white border border-navy/10 rounded-lg p-4">
                  <div className="w-20 h-20 rounded-lg overflow-hidden bg-cream shrink-0"><img src={mediaUrl(i.cover)} alt={i.name} className="w-full h-full object-cover" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{i.name}</div>
                    <div className="text-sm text-navy/50">{money(i.price)} MXN</div>
                    <div className="flex items-center gap-3 mt-2">
                      <div className="inline-flex items-center border border-navy/10 rounded-full">
                        <button onClick={() => setQty(i.product_id, i.qty - 1)} className="px-2 py-1 text-navy/50 hover:text-navy"><Minus className="w-3.5 h-3.5" /></button>
                        <span className="px-2 text-sm" data-testid={`cart-qty-${i.product_id}`}>{i.qty}</span>
                        <button onClick={() => setQty(i.product_id, i.qty + 1)} className="px-2 py-1 text-navy/50 hover:text-navy"><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                      <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(i.product_id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <div className="font-medium">{money(i.price * i.qty)}</div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-4 border-t border-navy/10">
                <span className="text-navy/50">{t("shop.subtotal")}</span>
                <span className="text-xl font-medium" data-testid="cart-subtotal">{money(subtotal)} <span className="text-navy/30 text-xs">MXN</span></span>
              </div>

              {/* How it works — process explanation */}
              <div className="mt-6 bg-white border border-navy/10 rounded-xl p-6" data-testid="cart-process">
                <div className="overline text-navy/40 mb-1">{t("shop.process.title")}</div>
                <p className="text-sm text-navy/60 mb-5">{t("shop.process.intro")}</p>
                <div className="space-y-4">
                  {Array.isArray(steps) && steps.map((s, idx) => {
                    const Icon = STEP_ICONS[idx] || ClipboardCheck;
                    return (
                      <div key={idx} className="flex gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-violet text-white flex items-center justify-center shrink-0"><Icon className="w-4 h-4" /></div>
                        <div>
                          <div className="font-medium text-sm">{idx + 1}. {s.title}</div>
                          <div className="text-sm text-navy/55 leading-relaxed">{s.desc}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* purchase-order form */}
            <motion.form onSubmit={submitOrder} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease }}
              data-testid="checkout-form" className="bg-white border border-navy/10 rounded-xl p-6 space-y-3 h-fit">
              <h3 className="text-lg font-medium mb-1">{t("shop.form.title")}</h3>
              <p className="text-xs text-navy/50 mb-2">{t("shop.form.orderNote")}</p>
              <input required placeholder={t("shop.form.name")} data-testid="checkout-name" value={c.name} onChange={set("name")} className={inputCls} />
              <input required type="email" placeholder={t("shop.form.email")} data-testid="checkout-email" value={c.email} onChange={set("email")} className={inputCls} />
              <input placeholder={t("shop.form.phone")} value={c.phone} onChange={set("phone")} className={inputCls} />
              <input required placeholder={t("shop.form.address")} data-testid="checkout-address" value={c.address} onChange={set("address")} className={inputCls} />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder={t("shop.form.city")} value={c.city} onChange={set("city")} className={inputCls} />
                <input placeholder={t("shop.form.postal")} value={c.postal_code} onChange={set("postal_code")} className={inputCls} />
              </div>
              <input placeholder={t("shop.form.country")} value={c.country} onChange={set("country")} className={inputCls} />

              {/* Cotización de envío (Envia.com) */}
              <div className="pt-2 border-t border-navy/10 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium flex items-center gap-1.5"><Truck className="w-4 h-4 text-violet" /> Envío</span>
                  <button type="button" onClick={quoteShipping} disabled={quoting} data-testid="quote-shipping-btn"
                    className="text-xs rounded-full border border-violet/40 text-violet px-3 py-1.5 hover:bg-violet/5 disabled:opacity-50">
                    {quoting ? "Cotizando…" : "Cotizar envío"}
                  </button>
                </div>
                {quoteErr && <p className="text-xs text-amber-600 mb-2" data-testid="quote-error">{quoteErr}</p>}
                {rates.length > 0 && (
                  <div className="space-y-1.5" data-testid="shipping-rates">
                    {rates.map((r, idx) => (
                      <label key={idx} data-testid={`shipping-rate-${idx}`}
                        className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 cursor-pointer text-sm ${selRate === r ? "border-violet bg-violet/5" : "border-navy/10 hover:border-navy/30"}`}>
                        <span className="flex items-center gap-2 min-w-0">
                          <input type="radio" name="ship" checked={selRate === r} onChange={() => setSelRate(r)} className="accent-violet" />
                          <span className="min-w-0">
                            <span className="block truncate">{r.carrier_description} · {r.service_description}</span>
                            {r.delivery_estimate && <span className="block text-[11px] text-navy/40">Entrega: {r.delivery_estimate}</span>}
                          </span>
                        </span>
                        <span className="font-medium shrink-0">{money(r.cost)} {r.currency}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center justify-between text-sm pt-2">
                <span className="text-navy/50">{t("shop.subtotal")}</span>
                <span className="font-medium">{money(subtotal)} MXN</span>
              </div>
              {selRate && (
                <div className="flex items-center justify-between text-sm" data-testid="checkout-shipping-line">
                  <span className="text-navy/50">Envío ({selRate.carrier_description})</span>
                  <span className="font-medium">{money(shippingCost)} MXN</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-navy/10">
                <span className="text-navy/60 font-medium">Total</span>
                <span className="text-xl font-medium" data-testid="checkout-total">{money(grandTotal)} <span className="text-navy/30 text-xs">MXN</span></span>
              </div>
              <p className="text-xs text-navy/40">{selRate ? "El envío seleccionado se incluye en tu orden de compra." : t("shop.shippingNote")}</p>
              {err && <p className="text-sm text-destructive" data-testid="checkout-error">{err}</p>}
              <button data-testid="checkout-pay" disabled={loading}
                className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity disabled:opacity-60">
                {loading ? t("shop.form.sending") : t("shop.form.submitOrder")}
              </button>
            </motion.form>
          </div>
        )}
      </div>
    </div>
  );
}
